// The durable vendor-media address:
// `<CABAL origin>/media/exercise-video/<exercise id or slug>`.
//
// ymove signs its demonstration-video URLs with a hard 48-hour expiry, so this
// is the only string that may travel into a projection, an attach block,
// persisted node content or a model.

import { ShortLinkOrigin } from '../../../domain/short-link/enums.js';

/** The root-level path segment the vendor-media redirect routes mount on. */
export const VENDOR_MEDIA_PATH_SEGMENT = 'media';

/** The exercise-video route under it: `/media/exercise-video/<idOrSlug>`. */
export const EXERCISE_VIDEO_ROUTE_SEGMENT = 'exercise-video';

/**
 * OUR origins: an address written in any environment is still ours, while the
 * same path under another host is somebody else's URL and is PLAYED there.
 */
const CABAL_ORIGINS: readonly string[] = Object.values(ShortLinkOrigin);

const EXERCISE_VIDEO_PATH_SEGMENTS = 3;

export function exerciseVideoAddress(input: {
  origin: string;
  idOrSlug: string;
}): string {
  const handle = encodeURIComponent(input.idOrSlug);
  return `${input.origin}/${VENDOR_MEDIA_PATH_SEGMENT}/${EXERCISE_VIDEO_ROUTE_SEGMENT}/${handle}`;
}

interface ParsedExerciseVideoAddress {
  readonly origin: string;
  readonly handle: string;
}

/**
 * NOTHING HERE THROWS. It is called while a persisted block is being read, and
 * a block is text a model wrote: `%E0%A4%A` is a `URIError` out of
 * `decodeURIComponent`, and one such field must cost that field's video, never
 * the decode of the whole record.
 */
function parseExerciseVideoAddress(input: {
  value: string;
}): ParsedExerciseVideoAddress | null {
  let parsed: URL;
  try {
    parsed = new URL(input.value);
  } catch {
    return null;
  }
  if (!CABAL_ORIGINS.includes(parsed.origin)) return null;
  if (parsed.username.length > 0 || parsed.password.length > 0) return null;
  if (parsed.search.length > 0 || parsed.hash.length > 0) return null;
  const segments = parsed.pathname
    .split('/')
    .filter((segment) => segment.length > 0);
  if (segments.length !== EXERCISE_VIDEO_PATH_SEGMENTS) return null;
  if (segments[0] !== VENDOR_MEDIA_PATH_SEGMENT) return null;
  if (segments[1] !== EXERCISE_VIDEO_ROUTE_SEGMENT) return null;
  let handle: string;
  try {
    handle = decodeURIComponent(segments[2] ?? '');
  } catch {
    return null;
  }
  return handle.length === 0 ? null : { origin: parsed.origin, handle };
}

/**
 * The address as WE spell it, or null for anything that is not one of ours.
 * A survivor is RESPELLED rather than echoed, so what a reader stores is
 * always a string this codebase built.
 */
export function canonicalExerciseVideoAddress(input: {
  value: string;
}): string | null {
  const parsed = parseExerciseVideoAddress({ value: input.value });
  if (parsed === null) return null;
  return exerciseVideoAddress({ origin: parsed.origin, idOrSlug: parsed.handle });
}
